// The legal gate in front of EVERY watermark-removal run (Haim: "show a checkbox that
// the realtor must click, acknowledging the fact that he has full rights to remove
// watermarks and market those properties ... shown everytime before a watermark
// operation is done").
//
// One component + one hook, shared by BOTH operation surfaces — the single-listing
// studio (WatermarkRemovalStudio) and the multi-listing dialog (BulkWatermarkRemoval) —
// so the wording and the persistence semantics can never drift apart.
//
// Semantics:
//  - The checkbox is rendered before every run, but PRE-CHECKED once the user has
//    accepted before: the saved state is the point, not repeated friction.
//  - First acceptance writes `User.watermarkTermsAcceptedAt` (a timestamp, not a bare
//    boolean — it IS the stored proof of agreement). Written once; later sessions
//    only read it.
//  - Un-checking disables the removal buttons for this session but does NOT erase the
//    stored proof — a past agreement isn't undone by a UI toggle.
import { useEffect, useState } from "react";
import { Checkbox, Typography, FormControlLabel } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import useFirestore from "hooks/useFirestore";

export type WatermarkTermsState = {
  /** Gate removal actions on this. */
  accepted: boolean;
  setAccepted: (value: boolean) => void;
};

/**
 * Owns the checkbox state + the one-time persistence of the acceptance proof on the
 * user document. Hosts gate their removal buttons on `accepted`.
 */
export function useWatermarkTermsAccepted(): WatermarkTermsState {
  const { userSettings, updateUserSettings } = useFirestore();
  const savedAccepted = Boolean(userSettings?.watermarkTermsAcceptedAt);
  const [accepted, setAcceptedState] = useState(savedAccepted);

  // The user doc can land after mount — pre-check then. One direction only: a saved
  // acceptance never un-checks the box, and an un-check never clears the saved proof.
  useEffect(() => {
    if (savedAccepted) setAcceptedState(true);
  }, [savedAccepted]);

  const setAccepted = (value: boolean): void => {
    setAcceptedState(value);
    if (value && !savedAccepted) {
      // First acceptance — record the proof. Fire-and-forget: the run must not wait
      // on this write, and a transient failure re-prompts next time (never blocks).
      void updateUserSettings({ watermarkTermsAcceptedAt: new Date() }).catch((e) =>
        console.error("WatermarkTermsCheckbox - failed to save acceptance", e)
      );
    }
  };

  return { accepted, setAccepted };
}

type WatermarkTermsCheckboxProps = WatermarkTermsState;

/** The checkbox itself — identical wording on every surface that runs a removal. */
export function WatermarkTermsCheckbox({
  accepted,
  setAccepted
}: WatermarkTermsCheckboxProps): JSX.Element {
  const { t: translate } = useTranslation();

  return (
    <FormControlLabel
      sx={{ mx: 0, alignItems: "flex-start" }}
      control={
        <Checkbox
          size="small"
          sx={{ p: 0.5, mt: -0.25 }}
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
        />
      }
      label={
        <Typography variant="body2" color={accepted ? "text.secondary" : "text.primary"}>
          {translate(
            "watermarkRemoval.termsCheckbox",
            "אני מאשר/ת שברשותי מלוא הזכויות להסיר את סימני המים מתמונות הנכסים ולשווק נכסים אלו"
          )}
        </Typography>
      }
    />
  );
}

export default WatermarkTermsCheckbox;
